import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import { getSearchableItems } from "../config/navCatalog";

/**
 * CommandPalette — press Ctrl+K (or ⌘K) to jump to any screen.
 *
 * The app has ~111 routes but the menu can only ever surface a handful, so
 * this is the fastest path to anything. Results come from navCatalog and are
 * already filtered to what this user's role would see in the menu; the route
 * guards still do the real enforcement on arrival.
 */
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { userInfo } = useSelector((s) => s.auth);

  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "th";
  const items = useMemo(() => getSearchableItems(userInfo, lang), [userInfo, lang]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, 12);
    return items
      .filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          (it.keywords || "").toLowerCase().includes(q) ||
          it.group.toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [query, items]);

  // Global shortcut
  useEffect(() => {
    const onKey = (e) => {
      const key = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (key === "escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // focus after paint
      const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => setActive(0), [query]);

  if (!userInfo || !open) return null;

  const go = (item) => {
    setOpen(false);
    if (/^https?:\/\//.test(item.path)) {
      window.open(item.path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(item.path);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[active]) go(results[active]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-start justify-center pt-[12vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />

      <div
        className="relative w-full max-w-xl bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-zinc-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-zinc-800">
          <FaSearch className="text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={t("palette.placeholder")}
            className="flex-1 bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:block text-[10px] font-semibold text-slate-400 border border-slate-200 dark:border-zinc-700 rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        <div className="max-h-[52vh] overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              {t("palette.noMatch", { query })}
            </div>
          ) : (
            results.map((it, i) => (
              <button
                key={it.path}
                onClick={() => go(it)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                  i === active
                    ? "bg-slate-100 dark:bg-zinc-800"
                    : "hover:bg-slate-50 dark:hover:bg-zinc-800/60"
                }`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {it.label}
                  </span>
                  <span className="block text-[11px] text-slate-400 truncate">{it.group}</span>
                </span>
                {i === active && (
                  <kbd className="text-[10px] font-semibold text-slate-400 border border-slate-200 dark:border-zinc-700 rounded px-1.5 py-0.5">
                    ↵
                  </kbd>
                )}
              </button>
            ))
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 dark:border-zinc-800 text-[11px] text-slate-400 flex gap-4">
          <span>↑↓ {t("palette.move")}</span>
          <span>↵ {t("palette.open")}</span>
          <span className="ml-auto">Ctrl + K</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
